import React, { useCallback, useState, useLayoutEffect, useRef } from 'react'
import { useChain } from '../chainState'
import { SimulationApp } from './SimulationApp'
import { ChainTaskPanel } from './ChainTaskPanel'
import { ChainCompletionScreen } from './ChainCompletionScreen'

export const ChainSimulationRunner = () => {
  const [state, dispatch] = useChain()
  const { simulations, activeIndex, chainComplete, reviewIndex } = state

  const activeSim = simulations[activeIndex]

  // Track the active sim's live state for ChainTaskPanel
  const [activeSimState, setActiveSimState] = useState(null)

  // Gate: store outputState here instead of dispatching immediately.
  // The user must click Continue in ChainTaskPanel to advance.
  const [pendingCompletion, setPendingCompletion] = useState(null)

  // Use a ref for autoAdvance so the onAllComplete callback always reads the
  // latest value without depending on activeSim in its closure.  This avoids
  // stale-closure issues caused by UPDATE_ACTIVE_STATE recreating the
  // simulations array on every plugin state change.
  const autoAdvanceRef = useRef(false)
  autoAdvanceRef.current = !!(activeSim && activeSim.autoAdvance)

  // Reset active sim state and pending gate when the active index changes.
  // useLayoutEffect so the reset happens before paint — prevents a flash of
  // stale pendingCompletion when the reducer auto-advances.
  useLayoutEffect(() => {
    setActiveSimState(null)
    setPendingCompletion(null)
  }, [activeIndex])

  const onAllComplete = useCallback(
    outputState => {
      if (autoAdvanceRef.current) {
        dispatch({
          type: 'COMPLETE_SIMULATION',
          payload: { index: activeIndex, outputState },
        })
      } else {
        setPendingCompletion({ outputState })
      }
    },
    [dispatch, activeIndex]
  )

  const confirmCompletion = useCallback(() => {
    if (!pendingCompletion) return
    dispatch({
      type: 'COMPLETE_SIMULATION',
      payload: { index: activeIndex, outputState: pendingCompletion.outputState },
    })
    setPendingCompletion(null)
  }, [dispatch, activeIndex, pendingCompletion])

  // Manual advance: lets the student move on (or finish the chain) even when
  // the active sim's objectives are not all met, so they can never get stuck.
  // Requires a live snapshot so completedTasks reflect real per-task status and
  // the plugin output is reliable.  Pass the snapshot to the reducer directly
  // rather than relying on UPDATE_ACTIVE_STATE timing.
  const manualAdvance = useCallback(() => {
    if (!activeSimState) return
    dispatch({
      type: 'COMPLETE_SIMULATION',
      payload: {
        index: activeIndex,
        outputState: (activeSimState && activeSimState._pluginOutput) || null,
        stateSnapshot: activeSimState,
      },
    })
  }, [dispatch, activeIndex, activeSimState])

  // Safety net: if the onAllComplete ref check missed (e.g. stale closure in
  // PluginPanel's effect chain), auto-confirm before the browser paints so
  // the Continue button never flashes for autoAdvance steps.
  useLayoutEffect(() => {
    if (pendingCompletion && activeSim && activeSim.autoAdvance) {
      confirmCompletion()
    }
  }, [pendingCompletion, activeSim, confirmCompletion])

  const onStateChange = useCallback(
    simState => {
      setActiveSimState(simState)
      dispatch({
        type: 'UPDATE_ACTIVE_STATE',
        payload: { simState, forIndex: activeIndex },
      })
    },
    [dispatch, activeIndex]
  )

  const exitReview = useCallback(() => {
    dispatch({ type: 'SET_REVIEW_INDEX', payload: null })
  }, [dispatch])

  // No-op callback passed to SimulationApp in review mode so the plugin panel
  // stays visible.  Without an onAllComplete prop, SimulationInner's
  // hideFramework logic hides the PluginPanel for any sim whose state has
  // allComplete: true (which every completed sim does).
  const reviewNoop = useCallback(() => {}, [])

  // --- Render path A: Review mode ---
  if (reviewIndex != null) {
    const reviewSim = simulations[reviewIndex]
    const reviewSnapshot = reviewSim ? reviewSim.stateSnapshot : null

    if (!reviewSim || !reviewSnapshot) {
      // No snapshot available — exit review
      dispatch({ type: 'SET_REVIEW_INDEX', payload: null })
      return null
    }

    const reviewSimConfig = {
      title: reviewSim.title,
      description: reviewSim.description,
      plugin: reviewSim.plugin,
      pluginConfig: reviewSim.pluginConfig,
      tasks: reviewSim.tasks || [],
    }

    const backLabel = chainComplete ? 'Back to Summary' : 'Back to Current'

    return (
      <div className="simulation-chain-body">
        <div className="chain-review-banner">
          <div className="chain-review-banner-info">
            <span className="chain-review-badge">Read Only</span>
            <span className="chain-review-title">Reviewing: {reviewSim.title || `Simulation ${reviewIndex + 1}`}</span>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={exitReview}>
            <i className="fa-solid fa-arrow-left" aria-hidden="true" /> {backLabel}
          </button>
        </div>
        <div className="simulation-framework">
          {!chainComplete && (
            <ChainTaskPanel
              simState={activeSimState}
              pendingCompletion={!!pendingCompletion}
              onContinue={confirmCompletion}
            />
          )}
          <SimulationApp
            key={`review-${reviewIndex}`}
            config={reviewSimConfig}
            restoreState={reviewSnapshot}
            resumeState={reviewSnapshot._pluginOutput}
            onAllComplete={reviewNoop}
            hideTaskPanel
          />
        </div>
      </div>
    )
  }

  // --- Render path B: Chain complete ---
  if (chainComplete) {
    return (
      <div className="simulation-chain-body">
        <ChainCompletionScreen />
      </div>
    )
  }

  // --- Render path C: Active sim (existing behavior) ---
  if (!activeSim) {
    return null
  }

  // Build the config for the current simulation
  const simConfig = {
    title: activeSim.title,
    description: activeSim.description,
    plugin: activeSim.plugin,
    pluginConfig: activeSim.pluginConfig,
    tasks: activeSim.tasks || [],
  }

  // Restore state from a previous snapshot (mid-simulation persistence)
  const snapshot = activeSim.stateSnapshot || null
  const pluginResumeState = snapshot && snapshot._pluginOutput ? snapshot._pluginOutput : null

  return (
    <div className="simulation-chain-body">
      <div className="simulation-framework">
        <ChainTaskPanel
          simState={activeSimState}
          pendingCompletion={!!pendingCompletion}
          onContinue={confirmCompletion}
          canManualAdvance={!!activeSimState && !pendingCompletion}
          onManualAdvance={manualAdvance}
        />
        <SimulationApp
          key={activeIndex}
          config={simConfig}
          inputState={activeSim.inputState}
          onAllComplete={onAllComplete}
          restoreState={snapshot}
          resumeState={pluginResumeState}
          onStateChange={onStateChange}
          hideTaskPanel
        />
      </div>
    </div>
  )
}
